import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Markdown } from "@/components/Chat/messages/markdown/Markdown";
import { convertBlocksToMarkdown } from "@/components/Canvas/utils/canvasUtils";
import { useCanvas } from "@/contexts/CanvasContext";
import { getSession } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { highlightCodeSync } from "@/utils/shiki";
import { ChevronDown } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

// Language to file extension mapping
const LANGUAGE_EXTENSIONS = {
  javascript: ".js",
  jsx: ".js",
  typescript: ".ts",
  tsx: ".ts",
  python: ".py",
  html: ".html",
  css: ".css",
  json: ".json",
  java: ".java",
  go: ".go",
  rust: ".rs",
  ruby: ".rb",
  php: ".php",
  swift: ".swift",
  kotlin: ".kt",
  c: ".c",
  cpp: ".cpp",
  csharp: ".cs",
  shell: ".sh",
  bash: ".sh",
  sql: ".sql",
  yaml: ".yaml",
  xml: ".xml",
  markdown: ".md",
};

// Get language display label
function getLanguageLabel(language) {
  const labels = {
    javascript: "JavaScript",
    jsx: "JavaScript",
    typescript: "TypeScript",
    tsx: "TypeScript",
    python: "Python",
    html: "HTML",
    css: "CSS",
    json: "JSON",
    java: "Java",
    go: "Go",
    rust: "Rust",
    ruby: "Ruby",
    php: "PHP",
    swift: "Swift",
    kotlin: "Kotlin",
    c: "C",
    cpp: "C++",
    csharp: "C#",
    shell: "Shell",
    bash: "Bash",
    sql: "SQL",
    yaml: "YAML",
    xml: "XML",
    markdown: "Markdown",
  };
  return labels[language] || language?.charAt(0).toUpperCase() + language?.slice(1) || "Source";
}

// Get MIME type for language
function getMimeType(language) {
  const mimeTypes = {
    javascript: "text/javascript",
    jsx: "text/javascript",
    typescript: "text/typescript",
    tsx: "text/typescript",
    python: "text/x-python",
    html: "text/html",
    css: "text/css",
    json: "application/json",
    java: "text/x-java-source",
    go: "text/x-go",
    rust: "text/x-rust",
    ruby: "text/x-ruby",
    php: "text/x-php",
    swift: "text/x-swift",
    kotlin: "text/x-kotlin",
    c: "text/x-c",
    cpp: "text/x-c++",
    csharp: "text/x-csharp",
    shell: "text/x-shellscript",
    bash: "text/x-shellscript",
    sql: "text/x-sql",
    yaml: "text/yaml",
    xml: "application/xml",
    markdown: "text/markdown",
  };
  return mimeTypes[language] || "text/plain";
}

// Get download options based on content type and language
function getDownloadOptions(contentType, language) {
  switch (contentType) {
    case "code": {
      const ext = LANGUAGE_EXTENSIONS[language] || ".txt";
      const langLabel = getLanguageLabel(language);
      return [
        { id: "pdf", label: "PDF Document (.pdf)" },
        { id: "source", label: `${langLabel} File (${ext})` },
        { id: "markdown", label: "Markdown (with code fence)" },
      ];
    }
    case "spreadsheet":
      return [
        { id: "pdf", label: "PDF Document (.pdf)" },
        { id: "excel", label: "Excel Spreadsheet (.xlsx)" },
        { id: "csv", label: "CSV File (.csv)" },
      ];
    case "html":
      return [
        { id: "pdf", label: "PDF Document (.pdf)" },
        { id: "html", label: "HTML File (.html)" },
        { id: "markdown", label: "Markdown (.md)" },
      ];
    case "text":
    case "markdown":
    default:
      return [
        { id: "pdf", label: "PDF Document (.pdf)" },
        { id: "docx", label: "Word Document (.docx)" },
        { id: "markdown", label: "Markdown (.md)" },
      ];
  }
}

// Parse CSV content into rows and columns for table display
function parseCSVContent(csvContent) {
  if (!csvContent) return [];

  const rows = csvContent.split('\n').map((row) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      const nextChar = row[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result;
  });

  // Filter out empty rows
  return rows.filter((row) => row.some((cell) => cell && cell.trim().length > 0));
}

export const CanvasPreview = memo(function CanvasPreview({
  canvasData,
  onCanvasClick,
  className,
  messageId,
  message,
}) {
  const { openCanvas, canvasState, isCanvasOpen, canvasSourceMessageId } =
    useCanvas();
  const [isCopying, setIsCopying] = useState(false);

  // Extract canvas info from the actual data (check nested structure)
  const actualData = useMemo(() => {
    if (!canvasData) return null;
    if (canvasData.canvasData) {
      return canvasData.canvasData;
    } else if (canvasData.metadata?.originalData?.canvasData) {
      return canvasData.metadata.originalData.canvasData;
    }
    return canvasData;
  }, [canvasData]);

  // Check message tool_calls for latest saved canvas name
  const latestCanvasName = useMemo(() => {
    if (!message?.tool_calls) return null;

    try {
      const toolCalls =
        typeof message.tool_calls === "string"
          ? JSON.parse(message.tool_calls)
          : message.tool_calls;

      const canvasToolCall = toolCalls.find((tc) => {
        const toolName = tc.function?.name || tc.name || tc.type;
        return (
          (toolName === "canvas_create" ||
            toolName === "canvas_stream" ||
            toolName === "canvas_edit") &&
          tc.result
        );
      });

      if (canvasToolCall?.result) {
        const foundName =
          canvasToolCall.result.canvasData?.name ||
          canvasToolCall.result.canvas?.name ||
          canvasToolCall.result.name;
        return foundName;
      }
    } catch (e) {
      // Failed to parse message tool_calls
    }
    return null;
  }, [message?.tool_calls]);

  // Memoize content type, language, and title calculations
  const { contentType, language, title } = useMemo(() => {
    if (!actualData)
      return { contentType: "text", language: "javascript", title: "Canvas" };

    return {
      contentType: actualData.contentType || actualData.type || "text",
      language: actualData.language || "javascript",
      title:
        latestCanvasName || actualData.title || actualData.name || "Canvas",
    };
  }, [actualData, latestCanvasName]);

  // Check if this canvas is currently being edited
  const isBeingEdited = useMemo(() => {
    return isCanvasOpen && canvasSourceMessageId === messageId;
  }, [isCanvasOpen, canvasSourceMessageId, messageId]);

  // Get the canvas title with live updates if canvas is open
  const canvasTitle = useMemo(() => {
    if (isBeingEdited && canvasState.name) {
      return canvasState.name;
    }

    return title || "Document";
  }, [isBeingEdited, canvasState.name, title]);

  // Get the full content as string for rendering
  const fullContent = useMemo(() => {
    if (!actualData) return "";

    // If we have blocks, convert them to markdown
    if (actualData.blocks && Array.isArray(actualData.blocks)) {
      return convertBlocksToMarkdown(actualData.blocks);
    }

    // If content is a string, use it directly
    if (typeof actualData.content === "string") {
      return actualData.content;
    }

    // If content is an array of blocks, convert it
    if (Array.isArray(actualData.content)) {
      return convertBlocksToMarkdown(actualData.content);
    }

    return "";
  }, [actualData]);

  // For code canvases, generate highlighted HTML directly using Shiki
  const highlightedCodeHTML = useMemo(() => {
    if (contentType !== "code" || !fullContent) return null;
    return highlightCodeSync(fullContent, language, "dark");
  }, [contentType, fullContent, language]);

  // For spreadsheet canvases, parse CSV into table data
  const spreadsheetData = useMemo(() => {
    if (contentType !== "spreadsheet" || !fullContent) return null;
    return parseCSVContent(fullContent);
  }, [contentType, fullContent]);

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    if (!fullContent) return;

    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(fullContent);
      toast.success("Copied to clipboard");
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy to clipboard");
    } finally {
      setIsCopying(false);
    }
  }, [fullContent]);

  // Handle edit - open in canvas editor
  const handleEdit = useCallback(() => {
    let actualCanvasData = canvasData;

    if (canvasData.canvasData) {
      actualCanvasData = canvasData.canvasData;
    } else if (canvasData.metadata?.originalData?.canvasData) {
      actualCanvasData = canvasData.metadata.originalData.canvasData;
    }

    // Dispatch event to reset undo history
    try {
      const resetUndoEvent = new CustomEvent("canvas-reset-undo-history", {
        detail: {
          source: "canvas_preview_edit",
          timestamp: new Date().toISOString(),
          reason: "canvas_opened_from_preview",
          messageId: messageId,
        },
      });
      window.dispatchEvent(resetUndoEvent);
    } catch (error) {
      console.warn("Could not dispatch reset undo history event:", error);
    }

    const processedCanvasData = {
      content:
        actualCanvasData.blocks &&
        Array.isArray(actualCanvasData.blocks) &&
        actualCanvasData.blocks.length > 0
          ? actualCanvasData.blocks
          : actualCanvasData.content,
      type: actualCanvasData.contentType || actualCanvasData.type || "text",
      language: actualCanvasData.language || "javascript",
      name: actualCanvasData.title || actualCanvasData.name || "Canvas",
      version:
        actualCanvasData.version || actualCanvasData.metadata?.version || 1,
      metadata: {
        ...actualCanvasData.metadata,
        fromPreview: true,
        timestamp: new Date().toISOString(),
        sourceMessageId: messageId,
      },
      sourceMessageId: messageId,
    };

    if (onCanvasClick) {
      onCanvasClick(processedCanvasData);
    } else {
      openCanvas(processedCanvasData);
    }
  }, [canvasData, messageId, onCanvasClick, openCanvas]);

  // Helper function to download a blob
  const downloadBlob = useCallback((blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Handle download - uses backend API for PDF/DOCX/Excel conversion
  const handleDownload = useCallback(
    async (format) => {
      if (!fullContent) return;

      const filename = canvasTitle || "document";
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3002";

      try {
        switch (format) {
          case "markdown": {
            // For code canvases, wrap content in code fence
            let content = fullContent;
            if (contentType === "code") {
              content = `\`\`\`${language}\n${fullContent}\n\`\`\``;
            }
            const blob = new Blob([content], { type: "text/markdown" });
            downloadBlob(blob, `${filename}.md`);
            toast.success(`Downloaded as ${filename}.md`);
            break;
          }
          case "pdf": {
            toast.loading("Generating PDF...", { id: "pdf-download" });
            const session = await getSession();
            if (!session?.access_token) {
              throw new Error("Please sign in to download as PDF");
            }
            // For code canvases, wrap in code fence for proper PDF rendering
            let pdfContent = fullContent;
            if (contentType === "code") {
              pdfContent = `\`\`\`${language}\n${fullContent}\n\`\`\``;
            }
            const response = await fetch(`${apiBaseUrl}/api/ai/convert/markdown-to-pdf`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                markdown: pdfContent,
                title: filename,
              }),
            });

            if (!response.ok) {
              const error = await response.json().catch(() => ({}));
              throw new Error(error.error || "Failed to generate PDF");
            }

            const blob = await response.blob();
            downloadBlob(blob, `${filename}.pdf`);
            toast.success(`Downloaded as ${filename}.pdf`, { id: "pdf-download" });
            break;
          }
          case "docx": {
            toast.loading("Generating Word document...", { id: "docx-download" });
            const session = await getSession();
            if (!session?.access_token) {
              throw new Error("Please sign in to download as Word document");
            }
            const response = await fetch(`${apiBaseUrl}/api/ai/convert/markdown-to-docx`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                markdown: fullContent,
                title: filename,
              }),
            });

            if (!response.ok) {
              const error = await response.json().catch(() => ({}));
              throw new Error(error.error || "Failed to generate DOCX");
            }

            const blob = await response.blob();
            downloadBlob(blob, `${filename}.docx`);
            toast.success(`Downloaded as ${filename}.docx`, { id: "docx-download" });
            break;
          }
          case "source": {
            // Download raw code with language-specific extension
            const ext = LANGUAGE_EXTENSIONS[language] || ".txt";
            const mimeType = getMimeType(language);
            const blob = new Blob([fullContent], { type: mimeType });
            downloadBlob(blob, `${filename}${ext}`);
            toast.success(`Downloaded as ${filename}${ext}`);
            break;
          }
          case "csv": {
            // Download spreadsheet content as CSV
            const blob = new Blob([fullContent], { type: "text/csv" });
            downloadBlob(blob, `${filename}.csv`);
            toast.success(`Downloaded as ${filename}.csv`);
            break;
          }
          case "excel": {
            // Convert CSV to Excel via backend API
            toast.loading("Generating Excel spreadsheet...", { id: "excel-download" });
            const session = await getSession();
            if (!session?.access_token) {
              throw new Error("Please sign in to download as Excel");
            }
            const response = await fetch(`${apiBaseUrl}/api/ai/convert/csv-to-excel`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                csv: fullContent,
                title: filename,
              }),
            });

            if (!response.ok) {
              const error = await response.json().catch(() => ({}));
              throw new Error(error.error || "Failed to generate Excel");
            }

            const blob = await response.blob();
            downloadBlob(blob, `${filename}.xlsx`);
            toast.success(`Downloaded as ${filename}.xlsx`, { id: "excel-download" });
            break;
          }
          case "html": {
            // Download HTML content
            const blob = new Blob([fullContent], { type: "text/html" });
            downloadBlob(blob, `${filename}.html`);
            toast.success(`Downloaded as ${filename}.html`);
            break;
          }
          default: {
            const blob = new Blob([fullContent], { type: "text/plain" });
            downloadBlob(blob, `${filename}.txt`);
            toast.success(`Downloaded as ${filename}.txt`);
          }
        }
      } catch (error) {
        console.error("Download error:", error);
        toast.error(`Failed to download as ${format.toUpperCase()}`, { id: `${format}-download` });
      }
    },
    [fullContent, canvasTitle, contentType, language, downloadBlob]
  );

  // Early return if no data
  if (!canvasData || !fullContent) {
    return null;
  }

  // Show simple placeholder when canvas is being edited
  if (isBeingEdited) {
    return (
      <div
        className={cn(
          "rounded-xl border border-border/50 bg-card overflow-hidden",
          "shadow-sm",
          className
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate">{canvasTitle}</span>
          </div>
          <span className="text-xs text-muted-foreground">Editing...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl border border-border/50 bg-card overflow-hidden",
        "shadow-sm",
        className
      )}
    >
      {/* Header with title and action buttons */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate">{canvasTitle}</span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Copy button */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  disabled={isCopying}
                  className="h-8 px-3 text-muted-foreground hover:text-foreground text-xs"
                >
                  Copy
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Copy content to clipboard</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Edit button */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEdit}
                  className="h-8 px-3 text-muted-foreground hover:text-foreground text-xs"
                >
                  Edit
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Open in canvas editor</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Download dropdown */}
          <DropdownMenu>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-muted-foreground hover:text-foreground text-xs"
                    >
                      Download
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Download options</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              {getDownloadOptions(contentType, language).map((option) => (
                <DropdownMenuItem
                  key={option.id}
                  onClick={() => handleDownload(option.id)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Full content rendered */}
      <div className="p-4 md:p-6">
        {contentType === "code" && highlightedCodeHTML ? (
          // Code canvases: render with Shiki syntax highlighting directly
          <div
            className={cn(
              "rounded-lg overflow-x-auto",
              "[&_.shiki]:!m-0 [&_.shiki]:!rounded-lg [&_.shiki]:!min-w-full",
              "[&_pre]:!m-0 [&_pre]:!p-4 [&_pre]:!w-fit [&_pre]:!min-w-full",
              "[&_code]:!text-sm [&_code]:!leading-relaxed [&_code]:!whitespace-pre"
            )}
            dangerouslySetInnerHTML={{ __html: highlightedCodeHTML }}
          />
        ) : contentType === "spreadsheet" && spreadsheetData && spreadsheetData.length > 0 ? (
          // Spreadsheet canvases: render as table
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  {spreadsheetData[0].map((header, colIndex) => (
                    <th
                      key={colIndex}
                      className="px-4 py-2 text-left font-medium text-foreground border-r border-border last:border-r-0"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spreadsheetData.slice(1).map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="border-b border-border last:border-b-0 hover:bg-muted/30"
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="px-4 py-2 border-r border-border last:border-r-0"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // Text/other canvases: render with markdown
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <Markdown>{fullContent}</Markdown>
          </div>
        )}
      </div>
    </div>
  );
});

CanvasPreview.displayName = "CanvasPreview";

export default CanvasPreview;
